import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent, SortOrder } from "primereact/datatable";
import { CheckCircleIcon, NoSymbolIcon, PencilIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../app/hooks";
import { UserRole } from "../../../app/userSlice";
import { ClassLinkOrCleverIconWithTooltip } from "../../../components/ClassLinkOrCleverIcon";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import SearchFilter from "../../../components/input/SearchFilter";
import Select from "../../../components/input/Select";
import SimplePagination from "../../../components/SimplePagination";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import { CourseStatus } from "../../courses/coursesApiSlice";
import { UserCourseViewModel, UserDistrictViewModel, UserSchoolViewModel } from "../usersApiSlice";
import AddCoursesToUserButton from "./AddCoursesToUserButton";
import RemoveUserFromCourses from "./RemoveUserFromCourses";

interface UserCourseTableModel extends UserCourseViewModel {
	isRowSelected: boolean;
}

interface UserCoursesProps {
	userId: number;
	userName: string;
	userRole: UserRole;
	courses: UserCourseViewModel[];
	schools: UserSchoolViewModel[];
	districts: UserDistrictViewModel[];
	showSchoolColumn: boolean;
	canAddCourse: boolean;
	canRemoveCourse: boolean;
	setErrorMessage: (message: string) => void;
}

export default function UserCoursesSection(props: UserCoursesProps) {
	const _displayLength = 5;
	const { config } = useAppSelector((state) => state.site);
	const hasClassLinkOrCleverCourse = props.courses?.some(
		(c) => c.isClassLinkCourse || c.isCleverCourse
	);

	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState("All");
	const [isFiltered, setIsFiltered] = useState(false);
	const [sortField, setSortField] = useState<string>();
	const [sortOrder, setSortOrder] = useState<SortOrder>(0);

	const [selectedCourses, setSelectedCourses] = useState<UserCourseViewModel[]>([]);
	const [isRemovingCourse, setIsRemovingCourse] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();

	function resetTable() {
		setSelectedCourses([]);
		setIsRemovingCourse(false);
		setPage(0);
	}

	const filteredCourses = useMemo(() => {
		return (
			props.courses.filter((course) => {
				if (searchFilter) {
					const searchLower = searchFilter.toLowerCase();
					const matchesSearch =
						course.name.toLowerCase().includes(searchLower) ||
						course.externalID?.toLowerCase().includes(searchLower);
					if (!matchesSearch) return false;
				}

				if (statusFilter === CourseStatus.Open && course.status !== CourseStatus.Open)
					return false;
				if (
					statusFilter === CourseStatus.Inactive &&
					course.status !== CourseStatus.Inactive
				)
					return false;

				return true;
			}) ?? []
		);
	}, [props.courses, searchFilter, statusFilter]);

	const sortedCourses = useMemo(() => {
		if (!sortField || sortOrder === 0 || !sortOrder) {
			return filteredCourses;
		}

		const sorted = [...filteredCourses].sort((a, b) => {
			let aValue: any;
			let bValue: any;

			if (sortField === "createDate") {
				aValue = a.createDate ? new Date(a.createDate).getTime() : 0;
				bValue = b.createDate ? new Date(b.createDate).getTime() : 0;

				if (aValue === bValue) return 0;
				return (aValue - bValue) * sortOrder;
			} else {
				aValue =
					(a[sortField as keyof UserCourseViewModel] as string)?.toLowerCase() || null;
				bValue =
					(b[sortField as keyof UserCourseViewModel] as string)?.toLowerCase() || null;
			}

			// Push nulls to the end regardless of sort direction
			if (aValue === null && bValue === null) return 0;
			if (aValue === null) return 1;
			if (bValue === null) return -1;

			return (
				aValue.localeCompare(bValue, undefined, {
					numeric: true, // Allow numbers to be sorted in a numerical order
					sensitivity: "base",
				}) * sortOrder
			);
		});

		return sorted;
	}, [filteredCourses, sortField, sortOrder]);

	const onSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	const paginatedCourses = sortedCourses.slice(
		page * _displayLength,
		(page + 1) * _displayLength
	);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	useEffect(() => {
		setIsFiltered(!!searchFilter || statusFilter !== "All");
		setPage(0);
	}, [searchFilter, statusFilter]);

	const checkboxBodyTemplate = (row: UserCourseTableModel) => {
		if (!row.canEditCourse) return;
		return (
			<input
				type="checkbox"
				value={row.courseID}
				checked={row.isRowSelected}
				onChange={handleSelectOne}
				className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
				aria-label={`Select course: ${row.name}`}
			/>
		);
	};

	const cleverOrClassLinkBodyTemplate = (row: UserCourseTableModel) => {
		const tooltipContent = row.isCleverCourse
			? "This course has a Clever account"
			: row.isClassLinkCourse
				? "This course has a ClassLink account"
				: undefined;

		return (
			<ClassLinkOrCleverIconWithTooltip
				isClassLinkEntity={!!row.isClassLinkCourse}
				isCleverEntity={!!row.isCleverCourse}
				tooltipId={`course-${row.courseID}-outside-tooltip`}
				tooltipContent={tooltipContent}
			/>
		);
	};

	const createDateBodyTemplate = (row: UserCourseTableModel) => {
		return (
			<span>
				<FormattedDateAndTime date={row.createDate} />
			</span>
		);
	};

	const statusBodyTemplate = (row: UserCourseTableModel) => {
		return (
			<span className="flex items-center gap-x-2">
				{row.status === CourseStatus.Open ? (
					<CheckCircleIcon className="size-6 text-green-500" />
				) : (
					<NoSymbolIcon className="size-6 text-red-500" />
				)}
				{row.status}
			</span>
		);
	};

	const editBodyTemplate = (row: UserCourseTableModel) => {
		if (!row.canEditCourse) return;
		return (
			<button
				id={`edit-${row.courseID}-button`}
				className="mr-2 cursor-pointer"
				onClick={() => {
					const returnTo = location.pathname + location.search;
					navigate(`/schools/course?courseId=${row.courseID}`, {
						state: {
							returnTo: returnTo,
						},
					});
				}}>
				<PencilIcon className="size-5" />
			</button>
		);
	};

	const handleSelectOne = (e: ChangeEvent<HTMLInputElement>) => {
		const courseId = e.currentTarget.value;
		const isChecked = e.currentTarget.checked;
		const course = props.courses.find((c) => String(c.courseID) === courseId);
		if (!course) return;
		if (isChecked) {
			setSelectedCourses((prev) => [...prev, course]);
		} else {
			setSelectedCourses((prev) => prev.filter((c) => c.courseID !== course.courseID));
		}
	};

	const handleSelectAll = (isChecked: boolean) => {
		if (isChecked) {
			// Add any missing editable courses from filteredCourses to selectedCourses
			setSelectedCourses((prev) => {
				const newCourses = filteredCourses.filter(
					(fc) => fc.canEditCourse && !prev.some((sc) => sc.courseID === fc.courseID)
				);
				return [...prev, ...newCourses];
			});
		} else {
			// Remove all filteredCourses from selectedCourses
			setSelectedCourses((prev) =>
				prev.filter((sc) => !filteredCourses.some((fc) => fc.courseID === sc.courseID))
			);
		}
	};

	const allChecked = useMemo(() => {
		const editableCourses = filteredCourses.filter((c) => c.canEditCourse);
		return (
			editableCourses.length > 0 &&
			editableCourses.every((c) => selectedCourses.some((sc) => sc.courseID === c.courseID))
		);
	}, [filteredCourses, selectedCourses]);

	const coursesWithSelection: UserCourseTableModel[] = useMemo(() => {
		return paginatedCourses.map((course) => ({
			...course,
			isRowSelected: selectedCourses.some((c) => c.courseID === course.courseID),
		}));
	}, [paginatedCourses, selectedCourses]);

	return (
		<section>
			<hr className="my-6" />
			<h2>Courses</h2>
			<div className="flex gap-3 mt-4 items-end justify-between">
				<div className="flex gap-x-3">
					<Select
						value={statusFilter}
						setValue={(v) => setStatusFilter(v as string)}
						label="Status"
						simpleListBoxId="courseStatusFilter"
						options={[
							{
								value: "All",
								label: "All Courses",
							},
							{
								value: CourseStatus.Open,
								label: "Open Courses",
							},
							{
								value: CourseStatus.Inactive,
								label: "Inactive Courses",
							},
						]}
						fieldClasses="w-56"
					/>
					<SearchFilter
						id="courseSearch"
						name="courseSearch"
						searchFilter={searchFilter}
						setSearchFilter={setSearchFilter}
						label="Search"
						placeHolder="Search by course name or external ID..."
						className="w-96"
					/>
				</div>
				<div className="flex gap-x-3 items-end">
					{props.canAddCourse && (
						<AddCoursesToUserButton
							userId={props.userId}
							userRole={props.userRole}
							districts={props.districts}
							schools={props.schools}
							courses={props.courses}
							setErrorMessage={props.setErrorMessage}
						/>
					)}
					{props.canRemoveCourse && (
						<RemoveUserFromCourses
							selectedCourses={selectedCourses}
							isLoading={isRemovingCourse}
							name={props.userName}
							userId={props.userId}
							role={props.userRole}
							resetTable={resetTable}
							setIsLoading={setIsRemovingCourse}
						/>
					)}
				</div>
			</div>
			<DataTable
				value={coursesWithSelection}
				loading={isRemovingCourse}
				dataKey="courseID"
				emptyMessage="No courses found"
				stripedRows
				className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
				style={{ marginTop: "16px" }}
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				sortIcon={CustomSortIcon}>
				{props.canRemoveCourse && (
					<Column
						columnKey="course-checkbox"
						header={
							<input
								type="checkbox"
								checked={allChecked}
								onChange={(e) => handleSelectAll(e.target.checked)}
								className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
								aria-label="Select all courses"
							/>
						}
						body={checkboxBodyTemplate}
						className="w-12"
						headerClassName="text-center"
						bodyClassName="text-center"
					/>
				)}
				{!config?.isUtahComposeSite && hasClassLinkOrCleverCourse && (
					<Column
						body={cleverOrClassLinkBodyTemplate}
						headerClassName="!pr-0 w-4"
						className="!pr-0"></Column>
				)}
				<Column header="Name" field="name" sortable></Column>
				<Column header="External ID" field="externalID" sortable></Column>
				{props.showSchoolColumn && (
					<Column header="School" field="schoolName" sortable></Column>
				)}
				<Column
					header="Created"
					body={createDateBodyTemplate}
					sortable
					sortField="createDate"
				/>
				<Column
					header="Status"
					body={statusBodyTemplate}
					sortField="status"
					sortable></Column>
				<Column header="Grade" field="grade" sortable></Column>
				<Column body={editBodyTemplate} className="w-4 !pl-1 !pr-2"></Column>
			</DataTable>
			<SimplePagination
				itemCount={sortedCourses?.length}
				page={page}
				pageSize={_displayLength}
				isFiltered={isFiltered}
				totalCount={props.courses?.length}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
		</section>
	);
}
